import { useEffect, useRef, type RefObject } from 'react'

type Options = {
  rootMargin?: string
  once?: boolean
}

export function observeWhenReady(
  targetRef: RefObject<Element | null>,
  onVisible: () => void,
  options: Options,
): () => void {
  const { rootMargin = '240px', once = true } = options
  let observer: IntersectionObserver | undefined
  let cancelled = false

  const attach = () => {
    const el = targetRef.current
    if (!el || cancelled) return

    if (typeof IntersectionObserver === 'undefined') {
      onVisible()
      return
    }

    observer?.disconnect()
    observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          onVisible()
          if (once) observer?.disconnect()
        }
      },
      { rootMargin },
    )
    observer.observe(el)
  }

  attach()
  if (!targetRef.current) {
    const rafId = requestAnimationFrame(attach)
    return () => {
      cancelled = true
      cancelAnimationFrame(rafId)
      observer?.disconnect()
    }
  }

  return () => {
    cancelled = true
    observer?.disconnect()
  }
}

/**
 * Run a fetch (or side effect) when a section nears the viewport.
 * Keeps below-fold API calls off the critical path on initial load.
 */
export function useFetchWhenNearViewport(
  fetchFn: () => void | Promise<void>,
  targetRef: RefObject<Element | null>,
  options: Options = {},
) {
  const { once = true } = options
  const fetchedRef = useRef(false)
  const fetchRef = useRef(fetchFn)
  fetchRef.current = fetchFn

  useEffect(() => {
    return observeWhenReady(
      targetRef,
      () => {
        if (once && fetchedRef.current) return
        fetchedRef.current = true
        void fetchRef.current()
      },
      options,
    )
  }, [targetRef, options.rootMargin, once])
}
